import React, { useEffect, useState } from "react";
import {
  EVENT_CODES_NAMES,
  EXAM_NAMES,
} from "../../../constants/eventConstants";

const expectedHeaders = {
  eventCreate: [
    "event_name",
    "start_date",
    "end_date",
    "start_time",
    "end_time",
    "event_level",
    "event_round",
    "school_id",
  ],
  eventReschedule: [
    "event_name",
    "school_id",
    "current_start_date_time",
    "current_end_date_time",
    "reschedule_start_date_time",
    "reschedule_end_date_time",
    "level",
    "round",
  ],
  eventDelete: [
    "school_id",
    "old_start_date",
    "old_end_date",
    "start_time",
    "end_time",
  ],
  studentRegister: [
    "isd_code",
    "first_name",
    "last_name",
    "phone",
    "grade",
    "school_id",
  ],
  uploadOfflineResponses: [
    "first_name",
    "last_name",
    "isd_code",
    "phone",
    "grade",
    "admission_id",
    "school_id",
    "event_name",
    "level",
    "round",
    "exam_name",
  ],
  globalStudentsRegister: [
    "first_name",
    "last_name",
    "isd_code",
    "phone",
    "grade",
    "school_id",
    "admission_id",
  ],
};

// Set to track admission IDs across rows
let seenAdmissionIds = new Set();

// Reset the set when component mounts/unmounts or when csvData changes
const resetAdmissionIdsTracking = () => {
  seenAdmissionIds = new Set();
};

const ValidationStep = ({
  csvData,
  handleValidationResult,
  schoolId,
  uploadType,
}) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const validateEventCreation = (row, index) => {
    const errorMessages = [];
    const {
      event_name,
      start_date,
      end_date,
      start_time,
      end_time,
      event_level,
      event_round,
      school_id,
    } = row;

    const supportedEvents = ["SCORE", "HOTS", "Math Olympiad", "I-EMSAT"];
    if (!event_name || !supportedEvents.includes(event_name)) {
      errorMessages.push(
        "Invalid event name. Must be one of: SCORE, HOTS, Math Olympiad, I-EMSAT"
      );
    }

    const datePattern = /^\d{2}-\d{2}-\d{4}$/;
    if (!start_date || !datePattern.test(start_date)) {
      errorMessages.push("Invalid start date format. Must be dd-mm-yyyy");
    }
    if (!end_date || !datePattern.test(end_date)) {
      errorMessages.push("Invalid end date format. Must be dd-mm-yyyy");
    }

    const timePattern = /^\d{2}:\d{2} (AM|PM)$/;
    if (!start_time || !timePattern.test(start_time)) {
      errorMessages.push("Invalid start time format. Must be hh:mm AM/PM");
    }
    if (!end_time || !timePattern.test(end_time)) {
      errorMessages.push("Invalid end time format. Must be hh:mm AM/PM");
    }

    const validLevels = ["Prelims", "Semi-Finals", "Finals"];
    if (!event_level || !validLevels.includes(event_level)) {
      errorMessages.push(
        "Invalid event level. Must be: Prelims, Semi-Finals, or Finals"
      );
    }

    const roundPattern = /^Round \d+$/;
    if (!event_round || !roundPattern.test(event_round)) {
      errorMessages.push("Invalid event round. Must be in format: Round X");
    }

    return errorMessages;
  };

  const validateEventReschedule = (row, index) => {
    const errorMessages = [];
    const {
      event_name,
      school_id,
      current_start_date_time,
      current_end_date_time,
      reschedule_start_date_time,
      reschedule_end_date_time,
      level,
      round,
    } = row;

    // Validate event name against known event names
    if (!event_name) {
      errorMessages.push("Event name is required");
    } else {
      const validEventNames = Object.values(EVENT_CODES_NAMES);
      if (!validEventNames.includes(event_name)) {
        errorMessages.push(
          `Invalid event name. Must be one of: ${validEventNames.join(", ")}`
        );
      }
    }

    // Validate ISO datetime format (YYYY-MM-DDTHH:mm)
    const isoDateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
    if (
      !current_start_date_time ||
      !isoDateTimePattern.test(current_start_date_time)
    ) {
      errorMessages.push(
        "Invalid current start date time format. Must be YYYY-MM-DDTHH:mm"
      );
    }
    if (
      !current_end_date_time ||
      !isoDateTimePattern.test(current_end_date_time)
    ) {
      errorMessages.push(
        "Invalid current end date time format. Must be YYYY-MM-DDTHH:mm"
      );
    }

    if (
      !reschedule_start_date_time ||
      !isoDateTimePattern.test(reschedule_start_date_time)
    ) {
      errorMessages.push(
        "Invalid reschedule start date time format. Must be YYYY-MM-DDTHH:mm"
      );
    }
    if (
      !reschedule_end_date_time ||
      !isoDateTimePattern.test(reschedule_end_date_time)
    ) {
      errorMessages.push(
        "Invalid reschedule end date time format. Must be YYYY-MM-DDTHH:mm"
      );
    }

    // Validate current_end_date_time is greater than current_start_date_time
    if (
      current_start_date_time &&
      current_end_date_time &&
      isoDateTimePattern.test(current_start_date_time) &&
      isoDateTimePattern.test(current_end_date_time)
    ) {
      const currentStartDateTime = new Date(current_start_date_time);
      const currentEndDateTime = new Date(current_end_date_time);
      if (currentEndDateTime <= currentStartDateTime) {
        errorMessages.push(
          "Current end date time must be greater than current start date time"
        );
      }
    }

    // Validate reschedule_end_date_time is greater than reschedule_start_date_time
    if (
      reschedule_start_date_time &&
      reschedule_end_date_time &&
      isoDateTimePattern.test(reschedule_start_date_time) &&
      isoDateTimePattern.test(reschedule_end_date_time)
    ) {
      const rescheduleStartDateTime = new Date(reschedule_start_date_time);
      const rescheduleEndDateTime = new Date(reschedule_end_date_time);
      if (rescheduleEndDateTime <= rescheduleStartDateTime) {
        errorMessages.push(
          "Reschedule end date time must be greater than reschedule start date time"
        );
      }
    }

    // Validate level
    const validLevels = ["Prelims", "Semi-Finals", "Finals"];
    if (!level || !validLevels.includes(level)) {
      errorMessages.push(
        "Invalid level. Must be: Prelims, Semi-Finals, or Finals"
      );
    }

    // Validate round (should be a number)
    if (
      !round ||
      isNaN(round) ||
      !Number.isInteger(Number(round)) ||
      Number(round) < 1
    ) {
      errorMessages.push("Invalid round.");
    }

    return errorMessages;
  };

  const validateEventDeletion = (row, index) => {
    const errorMessages = [];
    const {
      school_id,
      old_start_date: start_date,
      old_end_date: end_date,
      start_time,
      end_time,
      school_name,
      request_date,
      request_time,
      old_olympiad,
    } = row;

    const datePattern = /^\d{2}-\d{2}-\d{4}$/;
    const timePattern = /^\d{2}:\d{2}$/;

    if (!start_date || !datePattern.test(start_date)) {
      errorMessages.push("Invalid start date format. Must be dd-mm-yyyy");
    }
    if (!end_date || !datePattern.test(end_date)) {
      errorMessages.push("Invalid end date format. Must be dd-mm-yyyy");
    }

    if (!start_time || !timePattern.test(start_time)) {
      errorMessages.push("Invalid start time format. Must be hh:mm AM/PM");
    }
    if (!end_time || !timePattern.test(end_time)) {
      errorMessages.push("Invalid end time format. Must be hh:mm AM/PM");
    }

    return errorMessages;
  };

  const validateOfflineResponses = (row, index) => {
    const errorMessages = [];
    const {
      first_name,
      last_name,
      isd_code,
      phone,
      grade,
      school_id,
      admission_id,
      exam_name,
      event_name,
      level,
      round,
      ...answers
    } = row;

    if (index === 0) {
      const questionKeys = Object.keys(answers).filter((k) =>
        k.startsWith("Q")
      );

      const validPattern = /^Q\d+$/;

      const invalidKeys = questionKeys.filter((k) => !validPattern.test(k));
      if (invalidKeys.length > 0) {
        errorMessages.push(
          `Invalid question keys found: ${invalidKeys.join(", ")}`
        );
      }

      const questionNumbers = questionKeys
        .filter((k) => validPattern.test(k))
        .map((k) => parseInt(k.replace("Q", "")))
        .sort((a, b) => a - b);

      const missingQs = [];
      const maxQ = questionNumbers[questionNumbers.length - 1] || 0;
      for (let i = 1; i <= maxQ; i++) {
        if (!questionNumbers.includes(i)) {
          missingQs.push(`Q${i}`);
        }
      }

      if (missingQs.length > 0) {
        errorMessages.push(
          `Missing questions in sequence: ${missingQs.join(", ")}`
        );
      }
    }

    // Validate event name against known event names
    if (!event_name) {
      errorMessages.push("Event name is required");
    } else {
      const validEventNames = Object.values(EVENT_CODES_NAMES);
      if (!validEventNames.includes(event_name)) {
        errorMessages.push(
          `Invalid event name. Must be one of: ${validEventNames.join(", ")}`
        );
      }
    }

    // Validate level - reuse existing validation logic
    const validLevels = ["Prelims", "Semi-Finals", "Finals"];
    if (!level) {
      errorMessages.push("Level is required");
    } else if (!validLevels.includes(level)) {
      errorMessages.push(
        "Invalid level. Must be: Prelims, Semi-Finals, or Finals"
      );
    }

    // Validate round - reuse existing validation logic
    if (!round) {
      errorMessages.push("Round is required");
    } else {
      const roundPattern = /^Round \d+$/;
      if (!roundPattern.test(round)) {
        errorMessages.push("Invalid round. Must be in format: Round X");
      }
    }

    // Validate exam_name
    if (!exam_name) {
      errorMessages.push("Exam name is required");
    } else {
      if (!EXAM_NAMES.includes(exam_name)) {
        errorMessages.push(
          `Invalid exam name. Must be one of: ${EXAM_NAMES.join(", ")}`
        );
      }
    }

    // Check for duplicate admission_id
    if (admission_id && seenAdmissionIds.has(admission_id)) {
      errorMessages.push(`Duplicate admission ID - ${admission_id}`);
    } else if (admission_id) {
      seenAdmissionIds.add(admission_id);
    }

    // Validate answer fields (Q1, Q2, etc.)
    Object.entries(answers).forEach(([key, value]) => {
      if (key.match(/^Q\d+$|^$/)) {
        if (value !== "" && ![1, 2, 3, 4].includes(Number(value))) {
          answers[key] = "";
          // errorMessages.push(`${key} must be 1, 2, 3, or 4`);
        }
      }
    });

    // Validate first_name, last_name, isd_code, phone using same rules as student registration
    if (!first_name || !/^[A-Za-z\s]+$/.test(first_name)) {
      errorMessages.push("Invalid first name");
    }

    if (last_name && !/^[A-Za-z\s]+$/.test(last_name)) {
      errorMessages.push("Invalid last name");
    }

    // Validate ISD code
    if (!isd_code) {
      errorMessages.push("ISD code is required");
    } else {
      const cleanIsdCode = isd_code.toString().replace(/[\s+]/g, "");
      if (!/^\d{1,3}$/.test(cleanIsdCode)) {
        errorMessages.push(
          "ISD code must be 1-3 digits (e.g. 91 for India, 1 for USA)"
        );
      } else {
        const validIsdCodes = [
          "1",
          "44",
          "91",
          "971",
          "966",
          "965",
          "968",
          "973",
          "974",
        ];
        if (!validIsdCodes.includes(cleanIsdCode)) {
          errorMessages.push(
            `Invalid ISD code. Must be one of: ${validIsdCodes
              .map((code) => `${code}`)
              .join(", ")}`
          );
        }
      }
    }

    // Validate phone
    if (!phone) {
      errorMessages.push("Phone number is required");
    } else {
      const cleanPhone = phone.toString().replace(/[\s-]/g, "");
      const cleanIsdCode = isd_code.toString().replace(/[\s+]/g, "");

      if (cleanIsdCode === "91") {
        if (!/^\d{10}$/.test(cleanPhone)) {
          errorMessages.push("Indian phone numbers must be exactly 10 digits");
        }
      } else if (cleanIsdCode === "971") {
        if (!/^\d{9}$/.test(cleanPhone)) {
          errorMessages.push("UAE phone numbers must be exactly 9 digits");
        } else if (parseInt(cleanPhone[0]) < 5) {
          errorMessages.push(
            "UAE phone numbers must start with a digit greater than or equal to 5"
          );
        }
      } else {
        if (!/^\d{7,15}$/.test(cleanPhone)) {
          errorMessages.push("Phone number must be between 7-15 digits");
        }
      }
    }

    // Validate grade
    const parsedGrade = Number(grade);
    if (isNaN(parsedGrade) || parsedGrade < 1 || parsedGrade > 21) {
      errorMessages.push("Invalid Grade");
    }

    // Validate school_id
    const schoolIdPattern = /^[a-zA-Z]+\d+$/;
    if (!school_id) {
      errorMessages.push("Invalid school ID");
    } else if (!schoolIdPattern.test(school_id)) {
      errorMessages.push("Invalid school ID format");
    }

    // Validate admission_id
    if (!admission_id) {
      errorMessages.push("Admission ID is required");
    } else if (!/^[a-zA-Z0-9]+$/.test(admission_id)) {
      errorMessages.push("Admission ID must be alphanumeric");
    }

    return errorMessages;
  };

  const validateStudentRegistration = (row, index) => {
    const errorMessages = [];
    const { isd_code, first_name, last_name, phone, school_id, grade } = row;

    // Validate ISD code
    if (!isd_code) {
      errorMessages.push("ISD code is required");
    } else {
      // Remove any spaces and plus sign
      const cleanIsdCode = isd_code.toString().replace(/[\s+]/g, "");

      // Valid ISD codes are typically 1-3 digits
      if (!/^\d{1,3}$/.test(cleanIsdCode)) {
        errorMessages.push(
          "ISD code must be 1-3 digits (e.g. 91 for India, 1 for USA)"
        );
      } else {
        // List of valid ISD codes
        const validIsdCodes = [
          "1", // USA/Canada
          "44", // UK
          "91", // India
          "971", // UAE
          "966", // Saudi Arabia
          "965", // Kuwait
          "968", // Oman
          "973", // Bahrain
          "974", // Qatar
          // Add more valid codes as needed
        ];

        if (!validIsdCodes.includes(cleanIsdCode)) {
          errorMessages.push(
            `Invalid ISD code. Must be one of: ${validIsdCodes
              .map((code) => `${code}`)
              .join(", ")}`
          );
        }
      }
    }

    if (!first_name || !/^[A-Za-z\s]+$/.test(first_name)) {
      errorMessages.push("Invalid first name");
    }

    if (last_name && !/^[A-Za-z\s]+$/.test(last_name)) {
      errorMessages.push("Invalid last name");
    }

    if (!phone) {
      errorMessages.push("Phone number is required");
    } else {
      const cleanPhone = phone.toString().replace(/[\s-]/g, "");
      const cleanIsdCode = isd_code.toString().replace(/[\s+]/g, "");

      // Country-specific phone number validation
      if (cleanIsdCode === "91") {
        // India
        if (!/^\d{10}$/.test(cleanPhone)) {
          errorMessages.push("Indian phone numbers must be exactly 10 digits");
        }
      } else if (cleanIsdCode === "971") {
        // UAE
        if (!/^\d{9}$/.test(cleanPhone)) {
          errorMessages.push("UAE phone numbers must be exactly 9 digits");
        } else if (parseInt(cleanPhone[0]) < 5) {
          errorMessages.push(
            "UAE phone numbers must start with a digit greater than or equal to 5"
          );
        }
      } else {
        // For other countries, use general validation
        if (!/^\d{7,15}$/.test(cleanPhone)) {
          errorMessages.push("Phone number must be between 7-15 digits");
        }
      }
    }

    const schoolIdPattern = /^[a-zA-Z]+\d+$/;
    if (!school_id) {
      errorMessages.push("Invalid school ID");
    } else if (!schoolIdPattern.test(school_id)) {
      errorMessages.push("Invalid school ID format");
    } else if (school_id !== schoolId) {
      errorMessages.push(`School ID must be ${schoolId}`);
    }

    const parsedGrade = Number(grade);
    if (isNaN(parsedGrade) || parsedGrade < 1 || parsedGrade > 21) {
      errorMessages.push("Invalid Grade");
    }

    return errorMessages;
  };

  const validateGlobalStudentRegistration = (row, index) => {
    const errorMessages = [];
    const {
      first_name,
      last_name,
      isd_code,
      phone,
      grade,
      school_id,
      admission_id,
    } = row;

    if (!first_name || !/^[A-Za-z\s]+$/.test(first_name)) {
      errorMessages.push("Invalid first name");
    }

    if (last_name && !/^[A-Za-z\s]+$/.test(last_name)) {
      errorMessages.push("Invalid last name");
    }

    if (!isd_code) {
      errorMessages.push("ISD code is required");
    } else {
      const cleanIsdCode = isd_code.toString().replace(/[\s+]/g, "");
      if (!/^\d{1,3}$/.test(cleanIsdCode)) {
        errorMessages.push(
          "ISD code must be 1-3 digits (e.g. 91 for India, 1 for USA)"
        );
      } else {
        const validIsdCodes = [
          "1",
          "44",
          "91",
          "971",
          "966",
          "965",
          "968",
          "973",
          "974",
        ];
        if (!validIsdCodes.includes(cleanIsdCode)) {
          errorMessages.push(
            `Invalid ISD code. Must be one of: ${validIsdCodes
              .map((code) => `${code}`)
              .join(", ")}`
          );
        }
      }
    }

    if (!phone) {
      errorMessages.push("Phone number is required");
    } else {
      const cleanPhone = phone.toString().replace(/[\s-]/g, "");
      const cleanIsdCode = isd_code.toString().replace(/[\s+]/g, "");

      if (cleanIsdCode === "91") {
        if (!/^\d{10}$/.test(cleanPhone)) {
          errorMessages.push("Indian phone numbers must be exactly 10 digits");
        }
      } else if (cleanIsdCode === "971") {
        if (!/^\d{9}$/.test(cleanPhone)) {
          errorMessages.push("UAE phone numbers must be exactly 9 digits");
        } else if (parseInt(cleanPhone[0]) < 5) {
          errorMessages.push(
            "UAE phone numbers must start with a digit greater than or equal to 5"
          );
        }
      } else {
        if (!/^\d{7,15}$/.test(cleanPhone)) {
          errorMessages.push("Phone number must be between 7-15 digits");
        }
      }
    }

    const parsedGrade = Number(grade);
    if (isNaN(parsedGrade) || parsedGrade < 1 || parsedGrade > 21) {
      errorMessages.push("Invalid Grade");
    }

    const schoolIdPattern = /^[a-zA-Z]+\d+$/;
    if (!school_id) {
      errorMessages.push("Invalid school ID");
    } else if (!schoolIdPattern.test(school_id)) {
      errorMessages.push("Invalid school ID format");
    }

    if (!admission_id) {
      errorMessages.push("Admission ID is required");
    } else if (!/^[a-zA-Z0-9]+$/.test(admission_id)) {
      errorMessages.push("Admission ID must be alphanumeric");
    }

    if (admission_id && seenAdmissionIds.has(admission_id)) {
      errorMessages.push(`Duplicate admission ID - ${admission_id}`);
    } else if (admission_id) {
      seenAdmissionIds.add(admission_id);
    }

    return errorMessages;
  };

  const validateCsvData = (data) => {
    const errors = [];
    const validData = [];

    data.forEach((row, index) => {
      let errorMessages = [];

      switch (uploadType) {
        case "eventCreate":
          errorMessages = validateEventCreation(row, index);
          break;
        case "eventReschedule":
          errorMessages = validateEventReschedule(row, index);
          break;
        case "eventDelete":
          errorMessages = validateEventDeletion(row, index);
          break;
        case "uploadOfflineResponses":
          errorMessages = validateOfflineResponses(row, index);
          break;
        case "globalStudentsRegister":
          errorMessages = validateGlobalStudentRegistration(row, index);
          break;
        case "studentRegister":
        default:
          errorMessages = validateStudentRegistration(row, index);
      }

      if (errorMessages.length > 0) {
        errors.push({ index, errors: errorMessages, data: row });
      } else {
        validData.push(row);
      }
    });

    return { validData, errors };
  };

  useEffect(() => {
    resetAdmissionIdsTracking();

    if (!csvData || csvData.length === 0) {
      setIsValidating(false);
      setIsValid(false);
      handleValidationResult([], []);
      return;
    }

    const requiredHeaders = expectedHeaders[uploadType];
    if (requiredHeaders && csvData.length > 0) {
      const uploadedHeaders = Object.keys(csvData[0]);
      const missingHeaders = requiredHeaders.filter(
        (header) => !uploadedHeaders.includes(header)
      );

      if (missingHeaders.length > 0) {
        const headerErrors = csvData.map((row, index) => ({
          index,
          errors: [
            `Incorrect CSV format. Missing required columns: ${missingHeaders.join(
              ", "
            )}. Expected columns: ${requiredHeaders.join(", ")}`,
          ],
          data: row,
        }));

        const timer = setTimeout(() => {
          setIsValidating(false);
          setIsValid(false);
          setValidationErrors(headerErrors);
          handleValidationResult([], headerErrors);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }

    // Only check for schoolId in student registration
    if (uploadType === "studentRegister" && !schoolId) {
      const errorsWithSchoolId = csvData.map((row, index) => ({
        index,
        errors: ["Please enter School ID and click Get Data first"],
        data: row,
      }));

      const timer = setTimeout(() => {
        setIsValidating(false);
        setIsValid(false);
        setValidationErrors(errorsWithSchoolId);
        handleValidationResult([], errorsWithSchoolId);
      }, 1000);

      return () => clearTimeout(timer);
    }

    const { validData, errors } = validateCsvData(csvData);

    const timer = setTimeout(() => {
      setIsValidating(false);
      setIsValid(errors.length === 0);
      setValidationErrors(errors);
      handleValidationResult(validData, errors);
    }, 1000);

    return () => clearTimeout(timer);
  }, [csvData, handleValidationResult, schoolId, uploadType]);

  return (
    <section className="validation-step" aria-live="polite">
      <h2 className="validation-step__title">Validating CSV Data</h2>
      {isValidating && (
        <p className="validation-step__status">Scanning in progress…</p>
      )}
      {!isValidating && isValid && (
        <p className="validation-step__status validation-step__status--success">
          File is clean to upload.
        </p>
      )}
      {!isValidating && !isValid && (
        <p className="validation-step__status validation-step__status--error">
          We found issues in the file. Select “View” to check the errors.
        </p>
      )}
    </section>
  );
};

export default ValidationStep;
